// Payment Account Manager for Non-KYC Users
// Manages local payment methods (bank accounts, mobile wallets, etc.)

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as BankIcon,
  PhoneAndroid as MobileIcon,
  CheckCircle as VerifiedIcon,
  Warning as WarningIcon,
  Payment as PayPalIcon,
  CurrencyBitcoin as CryptoIcon
} from '@mui/icons-material';
import { API } from '../config/apiConfig';

const PaymentAccountManager = ({ userProfile, onAccountSelect, selectedAccountId }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state
  const [formData, setFormData] = useState({
    accountName: '',
    paymentMethod: '',
    accountNumber: '',
    bankName: '',
    accountHolderName: '',
    sortCode: '',
    iban: '',
    email: '',  // For PayPal
    usdtWalletAddress: '',  // For USDT
    usdtNetwork: 'TRC20',  // For USDT
    isDefault: false
  });

  // Payment method ID mapping (based on existing PaymentMethod table)
  const getPaymentMethodId = (paymentMethodName) => {
    const methodMap = {
      'Bank Transfer': 1,
      'PayPal': 2,
      'JazzCash': 3,
      'EasyPaisa': 4,
      'UPI': 5,
      'Paytm': 6,
      'bKash': 7,
      'Nagad': 8,
      'eSewa': 9,
      // 'USDT': 10  // Disabled - can enable later if needed
    };
    return methodMap[paymentMethodName] || 1; // Default to Bank Transfer
  };

  // State for payment methods from API
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  // Load payment methods from API - Uses new multi-currency endpoint
  const loadPaymentMethods = async () => {
    console.log('🔍 Loading available payment methods for current user (country-filtered)');
    
    try {
      setLoadingPaymentMethods(true);
      const url = API.paymentMethodsForUser; // New country-based endpoint
      console.log('📡 Calling country-based API endpoint:', url);
      
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Payment methods data (country-filtered):', data);
      
      if (data.success && data.paymentMethods && data.paymentMethods.length > 0) {
        console.log(`📍 User country: ${data.country} (${data.countryCode})`);
        
        // Transform API data to component format
        const methods = data.paymentMethods.map(method => ({
          value: method.paymentMethodName,
          label: `${method.paymentMethodName}${method.isRecommended ? ' ⭐ (Recommended)' : ''}`,
          id: method.paymentMethodId,
          currencyCode: method.currencyCode || 'USD',
          countryCode: data.countryCode,
          countryName: data.country,
          minimumPayout: method.minWithdrawalAmount || 0,
          maximumPayout: method.maxWithdrawalAmount || 0,
          processingFeePercent: method.processingFeePercent || 0,
          processingFeeFixed: method.processingFeeFixed || 0,
          processingTimeHours: method.processingTimeHours || 24,
          isRecommended: method.isRecommended || false,
          requiresKYC: method.requiresKYC || false,
          notes: method.notes || '',
          icon: method.paymentMethodName === 'PayPal' ? <PayPalIcon /> :
                method.paymentMethodName === 'Stripe' ? <PayPalIcon /> : // Use PayPal icon for now
                // method.paymentMethodName === 'USDT' ? <CryptoIcon /> :  // USDT disabled
                method.paymentMethodName.toLowerCase().includes('bank') ? <BankIcon /> : <MobileIcon />
        }));
        
        console.log(`✅ Available payment methods for ${data.countryCode}:`, methods.map(m => m.value).join(', '));
        setPaymentMethods(methods);
      } else {
        console.warn(`⚠️ No payment methods returned from API - adding PayPal as fallback`);
        // Always show PayPal as fallback - it's available globally
        setPaymentMethods([{
          value: 'PayPal',
          label: 'PayPal',
          id: 2, // PayPal payment method ID
          currencyCode: 'USD',
          icon: <PayPalIcon />
        }]);
      }
    } catch (error) {
      console.error('❌ Error loading payment methods:', error);
      // Always show PayPal as fallback even on error - it's available globally
      console.warn('⚠️ API error - adding PayPal as fallback');
      setPaymentMethods([{
        value: 'PayPal',
        label: 'PayPal',
        id: 2, // PayPal payment method ID
        currencyCode: 'USD',
        icon: <PayPalIcon />
      }]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Get required fields based on payment method and country
  const getRequiredFields = (paymentMethod, country) => {
    const fieldConfigs = {
      'PayPal': {
        // PayPal only needs email - works globally
        'ALL': ['email']
      },
      // 'USDT': {
      //   // USDT only needs wallet address - works globally (DISABLED)
      //   'ALL': ['usdtWalletAddress']
      // },
      'Bank Transfer': {
        'PK': ['accountNumber', 'bankName'],
        'IN': ['accountNumber', 'bankName'],
        'BD': ['accountNumber', 'bankName'],
        'LK': ['accountNumber', 'bankName'],
        'NP': ['accountNumber', 'bankName']
      },
      'JazzCash': {
        'PK': ['accountNumber']
      },
      'EasyPaisa': {
        'PK': ['accountNumber']
      },
      'UPI': {
        'IN': ['accountNumber']
      },
      'Paytm': {
        'IN': ['accountNumber']
      },
      'bKash': {
        'BD': ['accountNumber']
      },
      'Nagad': {
        'BD': ['accountNumber']
      },
      'eSewa': {
        'NP': ['accountNumber']
      }
    };
    
    // Check if PayPal (which works for all countries)
    if (paymentMethod === 'PayPal') {
      return fieldConfigs['PayPal']['ALL'];
    }
    // USDT disabled for now
    // if (paymentMethod === 'USDT') {
    //   return fieldConfigs['USDT']['ALL'];
    // }
    
    return fieldConfigs[paymentMethod]?.[country] || ['accountNumber'];
  };

  // Load user's payment accounts and payment methods
  useEffect(() => {
    console.log('🔍 PaymentAccountManager useEffect triggered');
    console.log('🔍 userProfile:', userProfile);
    
    loadAccounts();
    // Load payment methods - no userId needed, uses JWT token
    loadPaymentMethods();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch(API.savedPaymentAccounts, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setAccounts(data.accounts);
      } else {
        throw new Error(data.message || 'Failed to load payment accounts');
      }
    } catch (error) {
      console.error('Error loading payment accounts:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load payment accounts',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (account = null) => {
    if (account) {
      setEditingAccount(account);
      
      // For PayPal accounts: AccountNumber contains the email address
      const isPayPal = (account.PaymentMethodName || '').toLowerCase() === 'paypal';
      const emailValue = isPayPal ? (account.AccountNumber || '') : (account.Email || '');  // ✅ PayPal uses AccountNumber as email
      
      setFormData({
        accountName: account.AccountName || '',
        paymentMethod: account.PaymentMethodName || '',
        accountNumber: account.AccountNumber || '',
        bankName: account.BankName || '',
        accountHolderName: account.AccountName || '', // Use AccountName as holder name
        sortCode: account.SortCode || '',
        iban: account.IBAN || '',
        email: emailValue,  // ✅ For PayPal: AccountNumber is the email
        usdtWalletAddress: account.USDTWalletAddress || '',
        usdtNetwork: account.USDTNetwork || 'TRC20',
        isDefault: account.IsDefault || false
      });
    } else {
      setEditingAccount(null);
      setFormData({
        accountName: '',
        paymentMethod: '',
        accountNumber: '',
        bankName: '',
        accountHolderName: '',
        sortCode: '',
        iban: '',
        email: '',
        usdtWalletAddress: '',
        usdtNetwork: 'TRC20',
        isDefault: false
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingAccount(null);
    setFormData({
      accountName: '',
      paymentMethod: '',
      accountNumber: '',
      bankName: '',
      accountHolderName: '',
      ifscCode: '',
      iban: '',
      swiftCode: '',
      routingNumber: '',
      branchName: '',
      phoneNumber: '',
      email: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      const requiredFields = getRequiredFields(formData.paymentMethod, userProfile?.country);
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        setSnackbar({
          open: true,
          message: `Please fill in: ${missingFields.join(', ')}`,
          severity: 'warning'
        });
        return;
      }

      // For PayPal, use email as accountNumber (AccountNumber contains the email address)
      const accountNumber = formData.paymentMethod === 'PayPal' 
        ? formData.email  // PayPal: AccountNumber = email address
        : formData.accountNumber;  // Other methods: use accountNumber as-is

      // Ensure PayPal has email
      if (formData.paymentMethod === 'PayPal' && !formData.email) {
        setSnackbar({
          open: true,
          message: 'Please enter your PayPal email address',
          severity: 'warning'
        });
        return;
      }

      const payload = {
        accountId: editingAccount ? editingAccount.AccountID : null,
        userId: userProfile?.id,
        paymentMethodId: getPaymentMethodId(formData.paymentMethod),
        accountName: formData.accountName,
        accountNumber: accountNumber,  // ✅ For PayPal, AccountNumber = email address
        sortCode: formData.sortCode,
        iban: formData.iban,
        bankName: formData.bankName,
        email: null,  // ✅ Not used for PayPal - AccountNumber contains the email
        usdtWalletAddress: formData.usdtWalletAddress,  // USDT wallet
        usdtNetwork: formData.usdtNetwork,  // USDT network
        isDefault: formData.isDefault || false
      };

      console.log('📤 Saving payment account:', {
        paymentMethod: formData.paymentMethod,
        accountNumber: accountNumber,
        isPayPal: formData.paymentMethod === 'PayPal'
      });

      const response = await fetch(API.userPaymentAccounts, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      // Read response as JSON (even if status is 400)
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse response JSON:', jsonError);
        throw new Error('Invalid response from server');
      }
      
      console.log('Response status:', response.status);
      console.log('Response data:', data);
      
      if (!response.ok || !data.success) {
        // Read error message from response (could be in data.error or data.message)
        const errorMessage = data.error || data.message || 'Failed to save payment account';
        console.log('Error message from server:', errorMessage);
        throw new Error(errorMessage);
      }
      
      setSnackbar({
        open: true,
        message: editingAccount ? 'Payment account updated successfully' : 'Payment account added successfully',
        severity: 'success'
      });
      
      handleClose();
      loadAccounts();
      
    } catch (error) {
      console.error('Error saving payment account:', error);
      console.error('Error message:', error.message);
      const errorMessage = error.message || 'Failed to save payment account';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this payment account?')) {
      return;
    }

    try {
      const response = await fetch(`${API.savedPaymentAccounts}/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await response.json();
      
      if (data.success) {
        setSnackbar({
          open: true,
          message: 'Payment account deleted successfully',
          severity: 'success'
        });
        
        loadAccounts();
      } else {
        throw new Error(data.message || 'Failed to delete payment account');
      }
      
    } catch (error) {
      console.error('Error deleting payment account:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete payment account',
        severity: 'error'
      });
    }
  };

  const handleSelectAccount = (account) => {
    if (onAccountSelect) {
      onAccountSelect(account);
    }
  };

  // Show PaymentAccountManager for all users - payment methods are filtered by country via API
  // KYC users can still add PayPal accounts if available in their country
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card elevation={2}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="div">
              Payment Accounts
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
              size="small"
            >
              Add Account
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manage your payment accounts for withdrawals. Add bank accounts or mobile wallets.
          </Typography>

          {accounts.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No payment accounts added yet. Add your first account to enable withdrawals.
            </Alert>
          ) : (
            <List>
              {accounts.map((account) => {
                const method = paymentMethods.find(
                  m => m.value === account.PaymentMethodName
                );
                
                return (
                  <ListItem
                    key={account.AccountID}
                    button
                    selected={selectedAccountId === account.AccountID}
                    onClick={() => handleSelectAccount(account)}
                    sx={{
                      border: '1px solid',
                      borderColor: selectedAccountId === account.AccountID ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {method?.icon}
                          <Typography variant="subtitle1">
                            {account.AccountName}
                          </Typography>
                          {account.IsDefault && (
                            <Chip label="Default" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {account.PaymentMethodName} • {
                              account.PaymentMethodName === 'PayPal' ? account.AccountNumber : // ✅ For PayPal, AccountNumber is the email
                              // account.PaymentMethodName === 'USDT' ? (account.USDTWalletAddress?.substring(0, 10) + '...' + account.USDTWalletAddress?.substring(account.USDTWalletAddress.length - 6)) :
                              account.AccountNumber
                            }
                          </Typography>
                          {/* USDT network display disabled
                          {account.PaymentMethodName === 'USDT' && account.USDTNetwork && (
                            <Typography variant="caption" color="text.secondary">
                              Network: {account.USDTNetwork}
                            </Typography>
                          )}
                          */}
                          {account.BankName && account.PaymentMethodName !== 'PayPal' /* && account.PaymentMethodName !== 'USDT' */ && (
                            <Typography variant="caption" color="text.secondary">
                              {account.BankName}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpen(account);
                        }}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(account.AccountID);
                        }}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAccount ? 'Edit Payment Account' : 'Add Payment Account'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Account Name"
                value={formData.accountName}
                onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                fullWidth
                required
                helperText="Give this account a name (e.g., 'Main Bank Account')"
              />

              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => {
                    const selectedMethod = e.target.value;
                    setFormData({ 
                      ...formData, 
                      paymentMethod: selectedMethod,
                      // Clear other fields when switching methods
                      accountNumber: selectedMethod === 'PayPal' ? '' : formData.accountNumber,
                      bankName: selectedMethod === 'PayPal' ? '' : formData.bankName,
                      sortCode: selectedMethod === 'PayPal' ? '' : formData.sortCode,
                      iban: selectedMethod === 'PayPal' ? '' : formData.iban,
                      email: selectedMethod !== 'PayPal' ? '' : formData.email
                    });
                  }}
                  label="Payment Method"
                  disabled={loadingPaymentMethods}
                >
                  {loadingPaymentMethods ? (
                    <MenuItem disabled>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        Loading payment methods...
                      </Box>
                    </MenuItem>
                  ) : paymentMethods.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No payment methods available
                      </Typography>
                    </MenuItem>
                  ) : (
                    paymentMethods.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {method.icon}
                          {method.label}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
                {!loadingPaymentMethods && paymentMethods.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    Unable to load payment methods. Please refresh the page.
                  </Typography>
                )}
              </FormControl>

              {/* PayPal - Only needs email */}
              {formData.paymentMethod === 'PayPal' ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    PayPal only requires your email address to receive payments.
                  </Typography>
                </Alert>
              ) : null}
              
              {formData.paymentMethod === 'PayPal' ? (
                <TextField
                  label="Email Address *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  fullWidth
                  required
                  helperText="Enter your PayPal email address"
                  placeholder="your.email@example.com"
                />
              ) : /* USDT disabled - uncomment when ready to enable
              formData.paymentMethod === 'USDT' ? (
                <>
                  <TextField
                    label="USDT Wallet Address"
                    value={formData.usdtWalletAddress}
                    onChange={(e) => setFormData({ ...formData, usdtWalletAddress: e.target.value })}
                    fullWidth
                    required
                    helperText="Enter your USDT TRC20 wallet address (starts with T)"
                    placeholder="TAbC123XyZ..."
                  />
                  <FormControl fullWidth>
                    <InputLabel>Network</InputLabel>
                    <Select
                      value={formData.usdtNetwork}
                      onChange={(e) => setFormData({ ...formData, usdtNetwork: e.target.value })}
                      label="Network"
                    >
                      <MenuItem value="TRC20">TRC20 (Tron) - Recommended (Low fees)</MenuItem>
                      <MenuItem value="ERC20">ERC20 (Ethereum) - High fees</MenuItem>
                      <MenuItem value="BEP20">BEP20 (Binance Smart Chain)</MenuItem>
                    </Select>
                  </FormControl>
                </>
              ) : */ (
                <>
                  {/* Other payment methods - need account details */}
                  <TextField
                    label="Account Number"
                    value={formData.accountNumber}
                    onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                    fullWidth
                    required
                  />

                  {formData.paymentMethod === 'Bank Transfer' && (
                    <TextField
                      label="Bank Name"
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      fullWidth
                      required
                    />
                  )}

                  <TextField
                    label="Sort Code"
                    value={formData.sortCode}
                    onChange={(e) => setFormData({ ...formData, sortCode: e.target.value })}
                    fullWidth
                    helperText="Optional - for UK bank accounts"
                  />

                  <TextField
                    label="IBAN"
                    value={formData.iban}
                    onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                    fullWidth
                    helperText="Optional - for international transfers"
                  />
                </>
              )}

              <FormControl fullWidth>
                <Box display="flex" alignItems="center" gap={1}>
                  <input
                    type="checkbox"
                    id="isDefault"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                  <label htmlFor="isDefault">
                    <Typography variant="body2">
                      Set as default payment account
                    </Typography>
                  </label>
                </Box>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingAccount ? 'Update' : 'Add'} Account
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default PaymentAccountManager;
